
import { SimulationConfig, Ball, MorphData, TrailCircle } from '../../types';
import { ShapeUtils } from '../physics/ShapeUtils';
import { TrailLogic } from './TrailLogic';

export const BasicModesLogic = {
  updateBall: (
    b: Ball,
    effectiveCfg: SimulationConfig,
    now: number,
    hue: number,
    i: number,
    stepTS: number
  ) => {
    if (effectiveCfg.ballRotationMode === 'SPIN') {
        b.angle = (b.angle || 0) + (b.rotationSpeed || 0.1) * stepTS;
    } else if (effectiveCfg.ballRotationMode === 'VELOCITY') {
        b.angle = Math.atan2(b.vel.y, b.vel.x);
    }

    if (effectiveCfg.ballWeightGrowthEnabled) {
        const spawnTime = b.spawnTime || now;
        const elapsed = (now - spawnTime) / 1000;
        const progress = Math.min(elapsed / (effectiveCfg.ballWeightGrowthTime || 60), 1.0);
        b.mass = (effectiveCfg.ballMinWeight || 1.0) + ((effectiveCfg.ballMaxWeight || 10.0) - (effectiveCfg.ballMinWeight || 1.0)) * progress;
    }

    // Sync radius if growth is NONE
    if (effectiveCfg.ballGrowthMode === 'NONE') {
        b.radius = effectiveCfg.ballRadius;
    }

    if (effectiveCfg.mode === 'DRAWER') {
        b.trailAccumulator = (b.trailAccumulator || 0) + stepTS;
        if (b.trailAccumulator > (effectiveCfg.trailInterval || 1.0)) {
            b.trailAccumulator = 0;
            if (!b.hitPoints) b.hitPoints = [];
            b.hitPoints.push({ x: b.pos.x, y: b.pos.y });
            if (b.hitPoints.length > (effectiveCfg.trailLength || 50)) b.hitPoints.shift();
        }
    }

    if (effectiveCfg.mode === 'TRAIL') {
        TrailLogic.updateBall(b, effectiveCfg, now, stepTS);
    }

    // Sync visual properties
    const useRGB = effectiveCfg.rgbLighting && effectiveCfg.useBallRGB;
    if (useRGB) {
        b.color = `hsl(${hue}, 100%, 50%)`;
    } else {
        // Only sync if not in individual mode or if we want to force global color
        if (!effectiveCfg.ballPlacementIndividualEnabled) {
            b.color = i === 0 ? effectiveCfg.ball1Color : (i === 1 ? effectiveCfg.ball2Color : effectiveCfg.ballColor);
        }
    }
    b.style = effectiveCfg.ballStyle;
    b.shape = effectiveCfg.ballShape;
  },

  handleGrowthAndShrink: (
    balls: Ball[],
    effectiveCfg: SimulationConfig,
    deltaTime: number,
    containerRadiusRef: React.MutableRefObject<number>,
    ballGrowthRateRef: React.MutableRefObject<number>,
    containerShrinkRateRef: React.MutableRefObject<number>,
    morphData: MorphData,
    centerX: number,
    centerY: number,
    onBorderBreak: (b: Ball) => void
  ) => {
    if (effectiveCfg.ballGrowthMode === 'TIME') {
        const minContainerR = ShapeUtils.getMinRadius(
            morphData?.from || effectiveCfg.containerShape,
            morphData?.to || effectiveCfg.containerShape,
            morphData?.factor || 0,
            containerRadiusRef.current,
            effectiveCfg.containerRadius
        ) - (effectiveCfg.containerLineWidth || 4) / 2;

        balls.forEach(b => {
            const isEscape = effectiveCfg.mode === 'ESCAPE';
            const maxAllowed = isEscape ? (effectiveCfg.ballMaxRadius || 1000) : Math.min(effectiveCfg.ballMaxRadius, minContainerR);
            const growth = ballGrowthRateRef.current * (deltaTime / 1000);
            const nextRadius = b.radius + growth;
            
            if (effectiveCfg.mode === 'TRAIL' && effectiveCfg.trailEnableBorderBreak && b.radius < maxAllowed && nextRadius >= maxAllowed) {
                onBorderBreak(b);
            }
            
            const actualGrowth = Math.min(maxAllowed, nextRadius) - b.radius;
            b.radius += actualGrowth;
 
            // Push ball away from wall if it's penetrating due to growth
            // SKIP THIS FOR ESCAPE MODE - Escape mode has its own collision logic with multiple rings
            if (!isEscape) {
                const relX = b.pos.x;
                const relY = b.pos.y;
                const dist = Math.sqrt(relX * relX + relY * relY);
                if (dist + b.radius > minContainerR) {
                    const pushBack = (dist + b.radius) - minContainerR + 0.5;
                    if (dist > 0.1) {
                        const dirX = relX / dist;
                        const dirY = relY / dist;
                        b.pos.x -= dirX * pushBack;
                        b.pos.y -= dirY * pushBack;
                    }
                }
            }
        });

        if (effectiveCfg.ballGrowthAcceleration !== 0) {
            ballGrowthRateRef.current += effectiveCfg.ballGrowthAcceleration * (deltaTime / 1000);
        }
    }

    if (effectiveCfg.containerShrinkMode === 'TIME') {
        const shrink = containerShrinkRateRef.current * (deltaTime / 1000) * 10; 
        containerRadiusRef.current = Math.max(effectiveCfg.minContainerRadius, containerRadiusRef.current - shrink);

        if (effectiveCfg.containerShrinkAcceleration !== 0) {
            containerShrinkRateRef.current += effectiveCfg.containerShrinkAcceleration * (deltaTime / 1000);
        }
    }
    
    if (effectiveCfg.containerShrinkMode === 'NONE' && Math.abs(containerRadiusRef.current - effectiveCfg.containerRadius) > 1 && effectiveCfg.mode !== 'ESCAPE') {
       containerRadiusRef.current = effectiveCfg.containerRadius;
    }
  },

  init: (
    effectiveCfg: SimulationConfig,
    centerX: number,
    centerY: number,
    containerRadiusRef: React.MutableRefObject<number>,
    createBall: (overrides: Partial<Ball>) => Ball,
    getInitialBallState: (index: number, total: number, config?: any) => { pos: { x: number, y: number }, vel: { x: number, y: number } }
  ): Ball[] => {
    if (effectiveCfg.mode === 'TRAIL') {
        const total = Math.max(1, effectiveCfg.initialBalls || 1);
        return Array.from({ length: total }).map((_, i) => {
            const individualConfig = effectiveCfg.ballPlacementIndividualEnabled && effectiveCfg.ballPlacementConfigs && effectiveCfg.ballPlacementConfigs[i] 
                ? effectiveCfg.ballPlacementConfigs[i] 
                : null;
            const { pos, vel } = getInitialBallState(i, total, individualConfig);
            return createBall({
                id: `trail-ball-${i}`,
                pos,
                vel,
                radius: effectiveCfg.ballRadius,
                color: individualConfig?.color || (i === 0 ? effectiveCfg.ball1Color : (i === 1 ? effectiveCfg.ball2Color : '#ffffff')),
            });
        });
    } else if (effectiveCfg.mode === 'SPAWNER') {
        const total = effectiveCfg.initialBalls;
        return Array.from({ length: total }).map((_, i) => {
            const individualConfig = effectiveCfg.ballPlacementIndividualEnabled && effectiveCfg.ballPlacementConfigs[i] 
                ? effectiveCfg.ballPlacementConfigs[i] 
                : null;
            const { pos, vel } = getInitialBallState(i, total, individualConfig);
            return createBall({
                id: `init-${i}`,
                pos,
                vel,
                color: individualConfig?.color || (i === 0 ? effectiveCfg.ball1Color : (i === 1 ? effectiveCfg.ball2Color : '#ffffff')),
            });
        });
    } else {
        const total = Math.max(1, effectiveCfg.initialBalls || 1);
        return Array.from({ length: total }).map((_, i) => {
            const individualConfig = effectiveCfg.ballPlacementIndividualEnabled && effectiveCfg.ballPlacementConfigs && effectiveCfg.ballPlacementConfigs[i] 
                ? effectiveCfg.ballPlacementConfigs[i] 
                : null;
            const { pos, vel } = getInitialBallState(i, total, individualConfig);
            return createBall({
                id: `init-${i}`,
                pos,
                vel,
                radius: effectiveCfg.ballRadius,
                color: individualConfig?.color || (i === 0 ? effectiveCfg.ball1Color : (i === 1 ? effectiveCfg.ball2Color : '#ffffff')),
            });
        });
    }
  },

  renderTrail: (
    ctx: CanvasRenderingContext2D,
    trailCircles: TrailCircle[],
    config: SimulationConfig,
    webglRenderer: any,
    cam: any,
    globalHue: number,
    balls: Ball[],
    zoomLevel: number = 1.0
  ) => {
    const trailsEnabled = config.trailLength > 0 || config.trailPersistence;
    if (trailsEnabled) {
        if (webglRenderer) {
            webglRenderer.renderTrails(trailCircles, config, cam, zoomLevel);
        } else {
            trailCircles.forEach((tc, idx) => {
                const pr = trailCircles.length > 1 ? idx / (trailCircles.length - 1) : 1;
                const scale = config.trailMatchBallSize ? 1.0 : (config.trailEndScale + (config.trailStartScale - config.trailEndScale) * pr);
                const finalRadius = tc.radius * scale * (config.trailWidthMultiplier || 1.0);
                
                ctx.beginPath(); ctx.arc(tc.pos.x, tc.pos.y, finalRadius, 0, Math.PI * 2);
                const useTrailRGB = config.rgbLighting && config.useTrailRGB;
                ctx.fillStyle = useTrailRGB ? `hsl(${tc.hue}, 100%, 60%)` : (config.trailCustomColor !== '#ffffff' ? config.trailCustomColor : config.ballColor);
                ctx.globalAlpha = config.trailOpacity; ctx.fill();
                
                if (config.trailStrokeWidth > 0 && (!config.trailPersistence || config.trailApplyBorderToPersistence)) {
                    ctx.lineWidth = config.trailStrokeWidth;
                    ctx.strokeStyle = config.trailStrokeColor;
                    ctx.globalAlpha = Math.min(1, config.trailOpacity + 0.2); 
                    ctx.stroke();
                }
            });
        }
    }
    
    if (config.mode === 'DRAWER') balls.forEach(b => b.hitPoints?.forEach((pt: any) => {
        ctx.beginPath(); ctx.moveTo(pt.x, pt.y); ctx.lineTo(b.pos.x, b.pos.y);
        ctx.strokeStyle = (config.rgbLighting && config.useContainerRGB) ? `hsl(${globalHue}, 100%, 50%)` : b.color; ctx.globalAlpha = config.drawerLineOpacity; ctx.lineWidth = config.drawerLineThickness; ctx.stroke(); 
    }));
    
    if (config.trailLength > 0) balls.forEach(b => b.trail?.forEach((pos: any, idx: number) => {
        const len = b.trail?.length || 1;
        const pr = len > 1 ? idx / (len - 1) : 1;
        const scale = config.trailMatchBallSize ? 1.0 : (config.trailEndScale + (config.trailStartScale - config.trailEndScale) * pr);
        const finalRadius = b.radius * scale * (config.trailWidthMultiplier || 1.0);
        
        ctx.beginPath(); ctx.arc(pos.x, pos.y, finalRadius, 0, Math.PI * 2);
        const useTrailRGB = config.rgbLighting && config.useTrailRGB;
        ctx.fillStyle = useTrailRGB ? `hsl(${(globalHue + idx * 4) % 360}, 100%, 50%)` : (config.trailCustomColor !== '#ffffff' ? config.trailCustomColor : b.color);
        if (config.trailGlowAmount > 0) { ctx.shadowBlur = config.trailGlowAmount; ctx.shadowColor = ctx.fillStyle as string; }
        ctx.globalAlpha = pr * config.trailOpacity; 
        ctx.fill(); 
        
        if (config.trailStrokeWidth > 0 && (!config.trailPersistence || config.trailApplyBorderToPersistence)) {
            ctx.lineWidth = config.trailStrokeWidth;
            ctx.strokeStyle = config.trailStrokeColor;
            ctx.globalAlpha = pr * Math.min(1, config.trailOpacity + 0.2);
            ctx.stroke();
        }
        
        ctx.shadowBlur = 0;
    }));
  },

  handleMorph: (
    deltaTime: number,
    effectiveCfg: SimulationConfig,
    morphTimerRef: React.MutableRefObject<number>,
    morphStateRef: React.MutableRefObject<{ active: boolean, from: string, to: string, progress: number }>,
    SHAPES: string[],
    setConfig: any
  ) => {
    if (effectiveCfg.shapeMorphEnabled) {
        const availableShapes = (effectiveCfg.morphableShapes && effectiveCfg.morphableShapes.length > 0) ? effectiveCfg.morphableShapes : SHAPES;
        if (effectiveCfg.shapeMorphContinuous) {
            morphStateRef.current.active = true;
            const cycleTime = Math.max(100, effectiveCfg.shapeMorphDuration || 1000);
            morphStateRef.current.progress += deltaTime / cycleTime;
            if (morphStateRef.current.progress >= 1) {
                morphStateRef.current.progress = 0;
                let currentIndex = availableShapes.indexOf(morphStateRef.current.to);
                if (currentIndex === -1) currentIndex = 0;
                const nextShape = availableShapes[(currentIndex + 1) % availableShapes.length];
                morphStateRef.current.from = morphStateRef.current.to;
                morphStateRef.current.to = nextShape;
                setConfig((prev: SimulationConfig) => ({ ...prev, containerShape: morphStateRef.current.from })); 
            }
        } else {
            morphTimerRef.current += deltaTime;
            const interval = (effectiveCfg.shapeMorphSpeed || 1.0) * 2000;
            if (morphTimerRef.current > interval && !morphStateRef.current.active) {
                morphTimerRef.current = 0;
                let currentIndex = availableShapes.indexOf(effectiveCfg.containerShape);
                if (currentIndex === -1) currentIndex = 0;
                const nextShape = availableShapes[(currentIndex + 1) % availableShapes.length];
                morphStateRef.current.active = true;
                morphStateRef.current.from = effectiveCfg.containerShape;
                morphStateRef.current.to = nextShape;
                morphStateRef.current.progress = 0;
            }
            if (morphStateRef.current.active) {
                morphStateRef.current.progress += deltaTime / (effectiveCfg.shapeMorphDuration || 1000);
                if (morphStateRef.current.progress >= 1) {
                    morphStateRef.current.progress = 1;
                    if (effectiveCfg.containerShape !== morphStateRef.current.to) {
                        setConfig((prev: SimulationConfig) => ({ ...prev, containerShape: morphStateRef.current.to }));
                    } else {
                        morphStateRef.current.active = false;
                    }
                }
            }
        }
    } else {
        morphStateRef.current.active = false;
        if (morphStateRef.current.from !== effectiveCfg.containerShape) {
             morphStateRef.current.from = effectiveCfg.containerShape;
             morphStateRef.current.to = effectiveCfg.containerShape;
             morphStateRef.current.progress = 0;
        }
    }
  },

  handleZoom: (effectiveCfg: SimulationConfig, escapeRingsCount: number, zoomLevelRef: React.MutableRefObject<number>) => {
    if (effectiveCfg.mode === 'ESCAPE' && effectiveCfg.escapeZoomEffectEnabled) {
        const total = effectiveCfg.escapeRingCount;
        const progress = Math.max(0, Math.min(1, (total - escapeRingsCount) / total));
        const targetZoom = 1.0 - (1.0 - effectiveCfg.escapeZoomOutMax) * progress;
        const speed = effectiveCfg.escapeZoomOutSpeed || 0.05;
        zoomLevelRef.current += (targetZoom - zoomLevelRef.current) * speed;
    } else {
        if (Math.abs(zoomLevelRef.current - 1.0) > 0.001) {
             zoomLevelRef.current += (1.0 - zoomLevelRef.current) * 0.1;
        } else {
             zoomLevelRef.current = 1.0;
        }
    }
  },

  enforceGlobalBoundaries: (
    balls: Ball[],
    effectiveCfg: SimulationConfig,
    rotRad: number,
    morphData: MorphData,
    containerRadius: number
  ) => {
    if (effectiveCfg.mode !== 'SPACE_SPIRAL' && effectiveCfg.mode !== 'ESCAPE') {
        balls.forEach(b => {
            const relX = b.pos.x;
            const relY = b.pos.y;
            const dist = Math.sqrt(relX * relX + relY * relY);
            const angle = Math.atan2(relY, relX);
            const lineWidth = effectiveCfg.containerLineWidth || 4;
            const effectiveShapeR = ShapeUtils.getInterpolatedRadiusAtAngle(angle - rotRad, morphData?.from || effectiveCfg.containerShape, morphData?.to || effectiveCfg.containerShape, morphData?.factor || 0, containerRadius, morphData?.baseRadius) - lineWidth / 2;
            
            if (dist + b.radius > effectiveShapeR) {
                const overlap = (dist + b.radius) - effectiveShapeR;
                const localPos = {
                    x: relX * Math.cos(-rotRad) - relY * Math.sin(-rotRad),
                    y: relX * Math.sin(-rotRad) + relY * Math.cos(-rotRad)
                };
                const localNormal = ShapeUtils.getNormal(localPos, morphData?.from || effectiveCfg.containerShape, containerRadius - lineWidth / 2, morphData?.to || effectiveCfg.containerShape, morphData?.factor || 0, morphData?.baseRadius);
                const worldNormal = {
                    x: localNormal.x * Math.cos(rotRad) - localNormal.y * Math.sin(rotRad),
                    y: localNormal.x * Math.sin(rotRad) + localNormal.y * Math.cos(rotRad)
                };
                b.pos.x -= worldNormal.x * (overlap + 0.5);
                b.pos.y -= worldNormal.y * (overlap + 0.5);
            }
        });
    }
  }
};
